<script setup lang="ts">
import {first} from "lodash";
import {ChevronDownIcon, ChevronRightIcon } from "lucide-vue-next";
import {computed, onMounted, type Ref, ref} from "vue";
import {ConfigurationCategoryType} from "../../../enums/ConfigurationCategoryType.ts";
import type {ConfigurationCategoryResponse} from "../../../responses/ConfigurationCategoryResponse.ts";
import {useConfigurationStore} from "../../stores/configuration.ts";
import FormError from "../FormError.vue";
import ConfigurationAddition from "./ConfigurationAddition.vue";
import ConfigurationColor from "./ConfigurationColor.vue";

interface Props {
    category: ConfigurationCategoryResponse
    error?: string
}

const props = withDefaults(defineProps<Props>(), {
    error: '',
});
const toggled: Ref<boolean> = ref(false);
const configurationStore = useConfigurationStore();

const selectedOptionCount = computed(() => {
    return Object.values(configurationStore.selectedAdditions).filter(addition => addition.category_id === props.category.id).length;
});

const selectFirstAddition = () => {
    if(props.category.first_addition_selected){
        configurationStore.toggleAddition(props.category, first(props.category.additions));
    }
}

onMounted(() => {
    configurationStore.events.on('reset', () => {
        toggled.value = false;

        selectFirstAddition();
    });

    configurationStore.events.on('initialized', () => {
        selectFirstAddition();
    });
})
</script>

<template>
    <div v-if="category.type === ConfigurationCategoryType.color" :data-cat="category.id" class="-scroll-mt-18 md:scroll-mt-0">
        <configuration-color :category="category" v-for="addition in category.additions" :addition="addition" :key="addition.id"></configuration-color>
        <FormError v-if="configurationStore.validationErrors[category.id]" :error="configurationStore.validationErrors[category.id]" class="py-2 w-full flex"></FormError>
    </div>
    <div class="mt-0 md:mt-4 border border-white md:border-secondary-100 shadow-lg shadow-none md:shadow-black/3 -scroll-mt-18 md:scroll-mt-0" :data-cat="category.id" v-else>
        <button type="button" class="flex w-full items-center justify-between bg-white px-0 py-4 md:px-5 md:py-5 text-left" @click="toggled = !toggled">
            <div class="text-lg font-bold" :class="{'text-error-700': configurationStore.validationErrors[category.id], 'text-secondary-700': !configurationStore.validationErrors[category.id]}">
                {{ category.name }}
                <span v-if="category.type === ConfigurationCategoryType.multiple_options">({{ selectedOptionCount }})</span>
            </div>
            <ChevronDownIcon v-if="toggled" class="h-5 w-5 text-secondary-400 transition" />
            <ChevronRightIcon v-else class="h-5 w-5 text-secondary-400 transition" />
        </button>
        <div class="space-y-2 bg-white p-0 md:p-5 md:pt-0" v-show="toggled">
            <configuration-addition :category="category" v-for="addition in category.additions" :addition="addition" :key="addition.id"></configuration-addition>
        </div>
        <FormError v-if="configurationStore.validationErrors[category.id]" :error="configurationStore.validationErrors[category.id]" class="py-2 w-full flex"></FormError>
    </div>
</template>
